<script>
  import { format } from 'svelte-i18n';
  import { Card, CardBody } from '@sveltestrap/sveltestrap';

  import { Capability } from '@client/components/capability';
  import { CustomFields, SelectField, SwitchField } from '@client/components/fields/standalone';
  import { ScrollableSection } from '@client/components/scrollspy';

  import { disabledValue, enabledValue } from '@client/pages/manage/domain-settings';

  import { DEFAULT_SIGNATURE_MODE_OPTIONS } from '@client/enums';

  export let settings = {};
  export let onUpdate = null;
</script>

<div id="outlook">
  <ScrollableSection id="outlook-general">
    <h1 class="mb-3">{$format('label.GENERAL')}</h1>

    <Card>
      <CardBody class="p-4 pt-1">
        <SwitchField
          id="outlookaddin-disableAutoInsertSignatures"
          label={$format('label.AUTOMATIC_SIGNATURES')}
          tooltip={$format('label.TOOLTIP.AUTOMATIC_SIGNATURES')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          inverted
          bind:checked={settings.outlookaddin.disableAutoInsertSignatures}
          {onUpdate}
        />

        <SelectField
          id="outlookaddin-defaultSignatureMode"
          label={$format('label.DEFAULT_SIGNATURE_MODE')}
          tooltip={$format('label.TOOLTIP.DEFAULT_SIGNATURE_MODE')}
          selected={DEFAULT_SIGNATURE_MODE_OPTIONS.find(
            ({ value }) => value === settings.outlookaddin.defaultSignatureMode
          )}
          options={DEFAULT_SIGNATURE_MODE_OPTIONS}
          bind:value={settings.outlookaddin.defaultSignatureMode}
          {onUpdate}
        />

        <SwitchField
          id="outlookaddin-enableMultipleSignatures"
          label={$format('label.MULTIPLE_SIGNATURES')}
          tooltip={$format('label.TOOLTIP.MULTIPLE_SIGNATURES')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          bind:checked={settings.outlookaddin.enableMultipleSignatures}
          {onUpdate}
        />

        <SwitchField
          id="outlookaddin-disablePlainTextPreview"
          label={$format('label.PLAIN_TEXT_PREVIEW')}
          tooltip={$format('label.TOOLTIP.PLAIN_TEXT_PREVIEW')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          inverted
          bind:checked={settings.outlookaddin.disablePlainTextPreview}
          {onUpdate}
        />

        <SwitchField
          id="outlookaddin-disableRemoveDefaultSignature"
          label={$format('label.REMOVE_DEFAULT_SIGNATURE')}
          tooltip={$format('label.TOOLTIP.REMOVE_DEFAULT_SIGNATURE')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          inverted
          bind:checked={settings.outlookaddin.disableRemoveDefaultSignature}
          {onUpdate}
        />

        <SwitchField
          id="outlookaddin-userControlledSettings"
          label={$format('label.USER_CONTROLLED_SETTINGS')}
          tooltip={$format('label.TOOLTIP.USER_CONTROLLED_SETTINGS')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          bind:checked={settings.outlookaddin.userControlledSettings}
          {onUpdate}
        />

        <SwitchField
          id="outlookaddin-enableVisualPreview"
          label={$format('label.VISUAL_PREVIEW')}
          tooltip={$format('label.TOOLTIP.VISUAL_PREVIEW')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          bind:checked={settings.outlookaddin.enableVisualPreview}
          {onUpdate}
        />

        <SwitchField
          id="outlookaddin-clearSignatureIfNoDomain"
          label={$format('label.REMOVE_SIGNATURE_FOR_UNKNOWN_DOMAIN')}
          tooltip={$format('label.TOOLTIP.REMOVE_SIGNATURE_FOR_UNKNOWN_DOMAIN')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          bind:checked={settings.outlookaddin.clearSignatureIfNoDomain}
          {onUpdate}
        />

        <SwitchField
          id="outlookaddin-persistSelectionAdminControlled"
          label={$format('label.ALLOW_DEFAULT_OVERRIDE')}
          tooltip={$format('label.TOOLTIP.ALLOW_DEFAULT_OVERRIDE')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          bind:checked={settings.outlookaddin.persistSelectionAdminControlled}
          {onUpdate}
        />

        <SwitchField
          id="outlookaddin-resetMultiSelectionOnSend"
          label={$format('label.ALLOW_DEFAULT_OVERRIDE_MUTLTI')}
          tooltip={$format('label.TOOLTIP.ALLOW_DEFAULT_OVERRIDE')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          inverted
          bind:checked={settings.outlookaddin.resetMultiSelectionOnSend}
          {onUpdate}
        />

        <SwitchField
          id="outlookaddin-enableInlineImages"
          label={$format('label.INLINE_IMAGES')}
          tooltip={$format('label.TOOLTIP.INLINE_IMAGES')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          bind:checked={settings.outlookaddin.enableInlineImages}
          {onUpdate}
        />

        <Capability action="manage" subject="staff">
          <SwitchField
            id="outlookaddin-experimental"
            label="Experimental"
            tooltip="This should only be used by engineering for testing experimental features and functionality."
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.outlookaddin.experimental}
            {onUpdate}
          ></SwitchField>
          <SwitchField
            id="outlookaddin-enableHybridSignatures"
            label={$format('label.HYBRID_SIGNATURES')}
            tooltip={$format('label.TOOLTIP.HYBRID_SIGNATURES')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.outlookaddin.enableHybridSignatures}
            {onUpdate}
          ></SwitchField>
        </Capability>
      </CardBody>
    </Card>
  </ScrollableSection>

  <ScrollableSection id="outlook-fields">
    <h1 class="mb-3">{$format('label.USER_AND_SIGNATURE_FIELDS')}</h1>

    <Card>
      <CardBody class="p-4 pt-1">
        <SwitchField
          id="outlookaddin-disableDynamicFields"
          label={$format('label.DYNAMIC_FIELDS')}
          tooltip={$format('label.TOOLTIP.DYNAMIC_FIELDS')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          inverted
          bind:checked={settings.outlookaddin.disableDynamicFields}
          {onUpdate}
        />

        <SwitchField
          id="outlookaddin-disableFieldEdits"
          label={$format('label.EDITABLE_FIELDS')}
          tooltip={$format('label.TOOLTIP.EDITABLE_FIELDS')}
          inverted
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          bind:checked={settings.outlookaddin.disableFieldEdits}
          {onUpdate}
        />

        <SwitchField
          id="outlookaddin-signatureFieldsOnly"
          label={$format('label.SIGNATURE_FIELDS_ONLY')}
          tooltip={$format('label.TOOLTIP.SIGNATURE_FIELDS_ONLY')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          bind:checked={settings.outlookaddin.signatureFieldsOnly}
          {onUpdate}
        />

        <SwitchField
          id="outlookaddin-enableuihelpers"
          label={$format('label.UI_HELPERS')}
          tooltip={$format('label.TOOLTIP.CUSTOM_COMPOSE_FIELDS')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          bind:checked={settings.outlookaddin.enableuihelpers}
          {onUpdate}
        />

        {#if settings.outlookaddin.enableuihelpers}
          <CustomFields
            id="outlookaddin-uihelpers"
            label={$format('label.UI_HELPERS_CONFIGURATION')}
            tooltip={$format('label.TOOLTIP.UI_HELPERS_CONFIGURATION')}
            bind:fields={settings.outlookaddin.uihelpers}
            {onUpdate}
          />
        {/if}
      </CardBody>
    </Card>
  </ScrollableSection>

  <ScrollableSection id="outlook-email-tracking">
    <h1 class="mb-3">{$format('label.EMAIL_TRACKING')}</h1>

    <Card>
      <CardBody class="p-4 pt-1">
        <SwitchField
          id="outlookaddin-enableActivityFeed"
          label={$format('label.ACTIVITY_FEED')}
          tooltip={$format('label.TOOLTIP.ACTIVITY_FEED')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          bind:checked={settings.outlookaddin.enableActivityFeed}
          {onUpdate}
        />

        <!-- <SwitchField
          id="outlookaddin-showDoNotTrack"
          label={$format('label.DO_NOT_TRACK')}
          tooltip={$format('label.TOOLTIP.DO_NOT_TRACK')}
          inverted
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          bind:checked={settings.outlookaddin.showDoNotTrack}
          {onUpdate}
        /> -->
      </CardBody>
    </Card>
  </ScrollableSection>

  <ScrollableSection id="outlook-shared">
    <h1 class="mb-3">{$format('label.ALIASES_AND_SHARED_INBOXES')}</h1>

    <Card>
      <CardBody class="p-4 pt-1">
        <SwitchField
          id="outlookaddin-disableFromSwitching"
          label={$format('label.FROM_SWITCHING')}
          tooltip={$format('label.TOOLTIP.FROM_SWITCHING')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          inverted
          bind:checked={settings.outlookaddin.disableFromSwitching}
          {onUpdate}
        />

        {#if !settings.outlookaddin.disableFromSwitching}
          <SwitchField
            id="outlookaddin-mergeParams"
            label={$format('label.MERGE_USER_FIELDS')}
            tooltip={$format('label.TOOLTIP.MERGE_USER_FIELDS')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.outlookaddin.mergeParams}
            {onUpdate}
          />

          {#if settings.outlookaddin.mergeParams}
            <SwitchField
              id="outlookaddin-ignoreblankmergefields"
              label={$format('label.IGNORE_EMPTY_FIELDS')}
              tooltip={$format('label.TOOLTIP.IGNORE_EMPTY_FIELDS')}
              valueOn={$format(enabledValue)}
              valueOff={$format(disabledValue)}
              bind:checked={settings.outlookaddin.ignoreblankmergefields}
              {onUpdate}
            />
          {/if}
        {/if}
      </CardBody>
    </Card>
  </ScrollableSection>
</div>

<style lang="scss" src="./outlook.scss"></style>
